import { useEffect, useRef, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';

// Seksi video portrait (9:16) dalam bingkai ponsel + daftar momen yang bisa
// diketuk untuk melompat ke detik tertentu. Video tidak autoplay dan
// preload='none' supaya file besar hanya diunduh saat pengunjung menekan play.
//
// props:
//   tone        'light' | 'dark'
//   src, poster, ariaLabel, durationSeconds
//   eyebrow, title, description, note
//   chapters    [{ time, label, text }]
//   cta         { label, href }  (link WhatsApp, dibuka di tab baru)

function formatTime(totalSeconds) {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = Math.floor(totalSeconds % 60);
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

function VideoShowcase({
  id,
  tone = 'light',
  src,
  poster,
  ariaLabel,
  durationSeconds,
  eyebrow,
  title,
  description,
  note,
  chapters = [],
  cta,
}) {
  const videoRef = useRef(null);
  const pendingSeekRef = useRef(null);
  const [started, setStarted] = useState(false);
  const [activeTime, setActiveTime] = useState(null);
  const dark = tone === 'dark';
  const accent = dark ? '#9fe8ff' : '#0077a6';
  const muted = dark ? 'rgba(255,255,255,0.72)' : '#4b5563';

  const play = () => {
    const video = videoRef.current;
    if (!video) return;
    const result = video.play();
    if (result && typeof result.catch === 'function') result.catch(() => {});
  };

  const seekTo = (chapter) => {
    const video = videoRef.current;
    if (!video) return;
    setActiveTime(chapter.time);
    pendingSeekRef.current = chapter.time;
    const go = () => {
      video.currentTime = chapter.time;
      play();
    };
    if (video.readyState >= 1) {
      go();
    } else {
      video.addEventListener('loadedmetadata', go, { once: true });
      video.load();
    }
  };

  useEffect(() => {
    const video = videoRef.current;
    if (!video || chapters.length === 0) return undefined;
    // Abaikan timeupdate selama seek ke momen masih berjalan, supaya sorotan
    // tidak sempat loncat balik ke 0:00 saat video baru di-load.
    const onTime = () => {
      if (pendingSeekRef.current !== null) return;
      let current = null;
      chapters.forEach((chapter) => {
        if (video.currentTime >= chapter.time) current = chapter.time;
      });
      setActiveTime(current);
    };
    const clearPending = () => {
      pendingSeekRef.current = null;
    };
    video.addEventListener('timeupdate', onTime);
    video.addEventListener('seeked', clearPending);
    video.addEventListener('error', clearPending);
    return () => {
      video.removeEventListener('timeupdate', onTime);
      video.removeEventListener('seeked', clearPending);
      video.removeEventListener('error', clearPending);
    };
  }, [chapters]);

  return (
    <Box
      component='section'
      id={id}
      aria-labelledby={id ? `${id}-title` : undefined}
      sx={{
        backgroundColor: dark ? '#0b1220' : '#fff',
        color: dark ? '#fff' : '#111827',
        px: { xs: 2, md: 6 },
        py: { xs: 7, md: 9 },
        scrollMarginTop: 96,
      }}
    >
      <Box
        sx={{
          maxWidth: 1240,
          mx: 'auto',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 320px) 1fr' },
          gap: { xs: 4, md: 6 },
          alignItems: 'center',
        }}
      >
        {/* Bingkai ponsel */}
        <Box
          sx={{
            position: 'relative',
            mx: 'auto',
            width: '100%',
            maxWidth: 320,
            borderRadius: '28px',
            p: '10px',
            backgroundColor: '#0b0f14',
            border: '1px solid rgba(255,255,255,0.14)',
            boxShadow: dark
              ? '0 40px 80px rgba(0,0,0,0.55), inset 0 0 0 1px rgba(255,255,255,0.04)'
              : '0 30px 60px rgba(16,24,40,0.28)',
          }}
        >
          <Box
            sx={{
              position: 'relative',
              borderRadius: '20px',
              overflow: 'hidden',
              backgroundColor: '#000',
              '&::before': { content: '""', display: 'block', paddingTop: '177.8%' },
            }}
          >
            <Box
              component='video'
              ref={videoRef}
              src={src}
              poster={poster}
              controls={started}
              playsInline
              preload='none'
              aria-label={ariaLabel}
              onPlay={() => setStarted(true)}
              sx={{
                position: 'absolute',
                top: 0,
                right: 0,
                bottom: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
                backgroundColor: '#000',
              }}
            />
            {!started && (
              <Box
                component='button'
                type='button'
                onClick={play}
                aria-label='Putar video'
                sx={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  bottom: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  m: 0,
                  p: 0,
                  border: 0,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background:
                    'linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.05) 55%, rgba(0,0,0,0.6) 100%)',
                }}
              >
                <Box
                  sx={{
                    width: 76,
                    height: 76,
                    borderRadius: '50%',
                    backgroundColor: 'rgba(255,255,255,0.94)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 14px 32px rgba(0,0,0,0.45)',
                  }}
                >
                  <PlayArrowRoundedIcon sx={{ fontSize: 46, color: '#111827', ml: 0.4 }} />
                </Box>
                <Typography
                  sx={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    bottom: 18,
                    textAlign: 'center',
                    color: '#fff',
                    fontWeight: 800,
                    fontSize: '0.85rem',
                    letterSpacing: 0.3,
                  }}
                >
                  Putar video{durationSeconds ? ` · ${formatTime(durationSeconds)}` : ''}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {/* Teks + daftar momen */}
        <Box>
          {eyebrow && (
            <Typography
              sx={{
                color: accent,
                fontWeight: 800,
                letterSpacing: 0.4,
                textTransform: 'uppercase',
                fontSize: '0.82rem',
              }}
            >
              {eyebrow}
            </Typography>
          )}
          <Typography
            id={id ? `${id}-title` : undefined}
            component='h2'
            sx={{
              mt: 1,
              fontSize: { xs: '2rem', md: '2.8rem' },
              fontWeight: 900,
              lineHeight: 1.05,
            }}
          >
            {title}
          </Typography>
          {description && (
            <Typography sx={{ mt: 1.5, maxWidth: 720, color: muted, lineHeight: 1.7 }}>
              {description}
            </Typography>
          )}

          {chapters.length > 0 && (
            <Box
              sx={{
                mt: 3,
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 1,
              }}
            >
              {chapters.map((chapter) => {
                const isActive = activeTime === chapter.time;
                return (
                  <Box
                    key={chapter.time}
                    component='button'
                    type='button'
                    onClick={() => seekTo(chapter)}
                    aria-pressed={isActive}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      textAlign: 'left',
                      width: '100%',
                      px: 1.6,
                      py: 1.2,
                      borderRadius: '12px',
                      border: '1px solid',
                      borderColor: isActive
                        ? accent
                        : dark
                          ? 'rgba(255,255,255,0.14)'
                          : 'rgba(17,24,39,0.1)',
                      backgroundColor: isActive
                        ? dark
                          ? 'rgba(159,232,255,0.14)'
                          : 'rgba(0,119,166,0.08)'
                        : dark
                          ? 'rgba(255,255,255,0.04)'
                          : '#f4f6f8',
                      color: 'inherit',
                      fontFamily: 'inherit',
                      cursor: 'pointer',
                      transition: 'border-color 0.2s ease, background-color 0.2s ease',
                      '&:hover': { borderColor: accent },
                    }}
                  >
                    <Box
                      sx={{
                        minWidth: 42,
                        mt: 0.2,
                        fontWeight: 900,
                        fontSize: '0.8rem',
                        color: accent,
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {formatTime(chapter.time)}
                    </Box>
                    <Box sx={{ ml: 1 }}>
                      <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', lineHeight: 1.3 }}>
                        {chapter.label}
                      </Typography>
                      {chapter.text && (
                        <Typography sx={{ mt: 0.3, fontSize: '0.82rem', lineHeight: 1.45, color: muted }}>
                          {chapter.text}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}

          {note && (
            <Typography sx={{ mt: 2, fontSize: '0.82rem', color: muted }}>{note}</Typography>
          )}

          {cta && (
            <Button
              href={cta.href}
              target='_blank'
              rel='noopener noreferrer'
              variant='contained'
              startIcon={<WhatsAppIcon />}
              sx={{
                mt: 2.5,
                px: 3,
                py: 1.2,
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 800,
                backgroundColor: dark ? '#9fe8ff' : '#0077a6',
                color: dark ? '#06202b' : '#fff',
                '&:hover': { backgroundColor: dark ? '#c3f1ff' : '#00628a' },
              }}
            >
              {cta.label}
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default VideoShowcase;
